import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { staffAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Edit,
  Trash2,
  Users,
  Scissors,
  Mail,
  Phone,
  Tag,
  UserPlus
} from 'lucide-react';

// Form component outside main component
function StaffForm({ formData, setFormData }) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Nome Completo *</Label>
        <Input
          placeholder="Nome e cognome"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
        />
      </div>
      <div className="space-y-2">
        <Label>Email *</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="email"
            placeholder="email@esempio.it"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            className="pl-10"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Telefono</Label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="+39 333..."
            value={formData.phone}
            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            className="pl-10"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Ruolo</Label>
        <Select
          value={formData.role}
          onValueChange={(v) => setFormData(prev => ({ ...prev, role: v }))}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="BARBER">Operatore</SelectItem>
            <SelectItem value="ADMIN_STORE">Admin Negozio</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export default function StaffManager({ staff, categories, services, onRefresh }) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [loading, setLoading] = useState(false);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'BARBER'
  });

  // Services configuration state
  const [servicesConfig, setServicesConfig] = useState({
    can_do_all_services: true,
    allowed_services: [],
    allowed_categories: []
  });

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: 'BARBER'
    });
  };

  const openCreate = () => {
    resetForm();
    setIsCreateOpen(true);
  };

  const openEdit = (member) => {
    setSelectedStaff(member);
    setFormData({
      name: member.user?.name || '',
      email: member.user?.email || '',
      phone: member.user?.phone || '',
      role: member.user?.role || 'BARBER'
    });
    setIsEditOpen(true);
  };

  const openDelete = (member) => {
    setSelectedStaff(member);
    setIsDeleteOpen(true);
  };

  const openServices = (member) => {
    setSelectedStaff(member);
    setServicesConfig({
      can_do_all_services: member.can_do_all_services !== false,
      allowed_services: member.allowed_services || [],
      allowed_categories: member.allowed_categories || []
    });
    setIsServicesOpen(true);
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Nome e email sono obbligatori');
      return;
    }

    setLoading(true);
    try {
      const result = await staffAPI.create({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        role: formData.role
      });
      
      // Show appropriate success message based on email status
      if (result.temp_password_generated) {
        if (result.email_sent) {
          toast.success('Operatore creato! Email con credenziali inviata.');
        } else {
          toast.success('Operatore creato! Email in modalità MOCK (configura Resend per invio reale).');
        }
      } else {
        toast.success('Operatore creato con successo');
      }
      
      setIsCreateOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      console.error('Error creating staff:', error);
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedStaff) return;

    setLoading(true);
    try {
      await staffAPI.update(selectedStaff.id, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        role: formData.role
      });
      toast.success('Operatore aggiornato');
      setIsEditOpen(false);
      onRefresh();
    } catch (error) {
      console.error('Error updating staff:', error);
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedStaff) return;

    setLoading(true);
    try {
      await staffAPI.delete(selectedStaff.id);
      toast.success('Operatore eliminato');
      setIsDeleteOpen(false);
      setSelectedStaff(null);
      onRefresh();
    } catch (error) {
      console.error('Error deleting staff:', error);
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveServices = async () => {
    if (!selectedStaff) return;

    setLoading(true);
    try {
      await staffAPI.updateServices(selectedStaff.id, servicesConfig);
      toast.success('Servizi aggiornati');
      setIsServicesOpen(false);
      onRefresh();
    } catch (error) {
      console.error('Error updating services:', error);
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento dei servizi");
    } finally {
      setLoading(false);
    }
  };

  const toggleService = (serviceId) => {
    setServicesConfig(prev => {
      const svc = prev.allowed_services.includes(serviceId)
        ? prev.allowed_services.filter(id => id !== serviceId)
        : [...prev.allowed_services, serviceId];
      return { ...prev, allowed_services: svc };
    });
  };

  const toggleCategory = (categoryId) => {
    setServicesConfig(prev => {
      const cats = prev.allowed_categories.includes(categoryId)
        ? prev.allowed_categories.filter(id => id !== categoryId)
        : [...prev.allowed_categories, categoryId];
      return { ...prev, allowed_categories: cats };
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Gestione Staff</CardTitle>
          <CardDescription>Gestisci gli operatori e i servizi che possono eseguire</CardDescription>
        </div>
        <Button onClick={openCreate} data-testid="create-staff-btn">
          <UserPlus className="w-4 h-4 mr-2" />
          Nuovo Operatore
        </Button>
      </CardHeader>
      <CardContent>
        {staff.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">Nessun operatore</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {staff.map(member => (
              <Card key={member.id} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{member.user?.name || 'Operatore'}</p>
                      <p className="text-sm text-muted-foreground truncate">{member.user?.email}</p>
                      {member.user?.phone && (
                        <p className="text-xs text-muted-foreground truncate">{member.user?.phone}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t mb-3">
                    <div className="text-xs text-muted-foreground">
                      {member.can_do_all_services !== false ? (
                        <Badge variant="secondary" className="text-xs">Tutti i servizi</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">
                          {(member.allowed_services?.length || 0) + (member.allowed_categories?.length || 0)} assegnati
                        </Badge>
                      )}
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {member.user?.role === 'ADMIN_STORE' ? 'Admin' : 'Operatore'}
                    </Badge>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => openServices(member)}
                      data-testid={`staff-services-btn-${member.id}`}
                    >
                      <Scissors className="w-3 h-3 mr-1" />
                      Servizi
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(member)}
                      data-testid={`edit-staff-${member.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDelete(member)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`delete-staff-${member.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Nuovo Operatore
            </DialogTitle>
            <DialogDescription>
              Aggiungi un nuovo membro dello staff. Verrà creato un account con password temporanea.
            </DialogDescription>
          </DialogHeader>
          <StaffForm formData={formData} setFormData={setFormData} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleCreate} disabled={loading}>
              {loading ? 'Creazione...' : 'Crea Operatore'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Modifica Operatore
            </DialogTitle>
          </DialogHeader>
          <StaffForm formData={formData} setFormData={setFormData} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleUpdate} disabled={loading}>
              {loading ? 'Salvataggio...' : 'Salva Modifiche'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
            <DialogDescription asChild>
              <div>
                Sei sicuro di voler eliminare questo operatore?
                {selectedStaff && (
                  <div className="mt-2 p-3 bg-muted rounded-md">
                    <span className="block"><strong>Nome:</strong> {selectedStaff.user?.name}</span>
                    <span className="block"><strong>Email:</strong> {selectedStaff.user?.email}</span>
                  </div>
                )}
                <span className="block mt-2 text-destructive">
                  Questa azione cancellerà anche tutti gli appuntamenti futuri assegnati a questo operatore.
                </span>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={loading}>
              {loading ? 'Eliminazione...' : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Services Configuration Dialog */}
      <Dialog open={isServicesOpen} onOpenChange={setIsServicesOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scissors className="w-5 h-5" />
              Servizi di {selectedStaff?.user?.name}
            </DialogTitle>
            <DialogDescription>
              Seleziona quali servizi questo operatore può eseguire
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Toggle all services */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border">
              <div>
                <p className="font-medium">Può eseguire tutti i servizi</p>
                <p className="text-sm text-muted-foreground">
                  Se attivo, l&apos;operatore può eseguire qualsiasi servizio del catalogo
                </p>
              </div>
              <Switch
                checked={servicesConfig.can_do_all_services}
                onCheckedChange={(checked) => setServicesConfig(prev => ({ ...prev, can_do_all_services: checked }))}
              />
            </div>

            {!servicesConfig.can_do_all_services && (
              <>
                {/* Categories Selection */}
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    Categorie intere
                  </h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Selezionando una categoria, l&apos;operatore potrà eseguire tutti i servizi al suo interno
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {categories.map(cat => (
                      <label
                        key={cat.id}
                        className="flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-muted/50"
                      >
                        <Checkbox
                          checked={servicesConfig.allowed_categories.includes(cat.id)}
                          onCheckedChange={() => toggleCategory(cat.id)}
                        />
                        <span className="text-sm">{cat.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Individual Services Selection */}
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Scissors className="w-4 h-4" />
                    Servizi specifici
                  </h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Seleziona singoli servizi che l&apos;operatore può eseguire
                  </p>
                  <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                    {services.map(service => (
                      <label
                        key={service.id}
                        className="flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-muted/50"
                      >
                        <Checkbox
                          checked={servicesConfig.allowed_services.includes(service.id)}
                          onCheckedChange={() => toggleService(service.id)}
                        />
                        <span className="text-sm">{service.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsServicesOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveServices} disabled={loading}>
              {loading ? 'Salvataggio...' : 'Salva Configurazione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
